// Order-table sorting for the orderSpy waitlist (spec 4.2).
//
// Kept as pure functions so the kitchen-workflow status order and the
// blanks-sink rule are unit-tested directly. The table sorts a COPY; the
// fetched array is never mutated.

export type SortDir = "asc" | "desc";
export type ColumnType = "number" | "text" | "status" | "date";

export interface WaitlistOrder {
  OrderNo?: number | string;
  CustomerName?: string;
  OrderStatus?: string;
  PromiseTime?: string;
  CookEndTime?: string;
  [key: string]: unknown;
}

export interface OrderColumn {
  key: keyof WaitlistOrder & string;
  label: string;
  type: ColumnType;
}

export const ORDER_COLUMNS: OrderColumn[] = [
  { key: "OrderNo", label: "Order #", type: "number" },
  { key: "CustomerName", label: "Customer", type: "text" },
  { key: "OrderStatus", label: "Status", type: "status" },
  { key: "PromiseTime", label: "Promise Time", type: "date" },
  { key: "CookEndTime", label: "Cook End Time", type: "date" },
];

// Default: newest order first, which is what an operator wants on landing.
export const DEFAULT_SORT = { key: "OrderNo", dir: "desc" as SortDir };

// Kitchen-workflow order, NOT alphabetical. Alphabetical would read "Canceled,
// Cooking, Making, Ready, Waiting", meaningless to a kitchen. Canceled sorts
// last because it is not active work. Vocabulary sampled from live orders;
// compared case-insensitively.
export const STATUS_RANK: Record<string, number> = {
  waiting: 0,
  making: 1,
  cooking: 2,
  ready: 3,
  canceled: 4,
};

// Unknown (non-empty) statuses sort after every known one rather than vanishing.
const UNKNOWN_STATUS_RANK = 99;

/** Returns a comparable value for a cell, or null when the cell is blank.
 *  null is the "absent" sentinel and always sinks to the bottom. */
export function sortValue(
  order: WaitlistOrder,
  key: string,
  type: ColumnType,
): number | string | null {
  const raw = order[key];
  if (type === "number") {
    const n = Number(raw);
    return Number.isFinite(n) && raw !== "" && raw != null ? n : null;
  }
  if (type === "date") {
    if (raw == null || raw === "") return null;
    const t = new Date(raw as string).getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (type === "status") {
    const s = (typeof raw === "string" ? raw : "").trim().toLowerCase();
    if (!s) return null;
    return s in STATUS_RANK ? STATUS_RANK[s] : UNKNOWN_STATUS_RANK;
  }
  // text
  const s = (typeof raw === "string" ? raw : raw == null ? "" : String(raw)).trim();
  return s === "" ? null : s.toLowerCase();
}

export function compareOrders(
  a: WaitlistOrder,
  b: WaitlistOrder,
  key: string,
  type: ColumnType,
  dir: SortDir,
): number {
  const av = sortValue(a, key, type);
  const bv = sortValue(b, key, type);

  // Blanks always sink to the bottom, whichever way we are sorting. A missing
  // customer name is not "smallest", it is absent. Direction-independent.
  if (av === null && bv === null) return 0;
  if (av === null) return 1;
  if (bv === null) return -1;

  let cmp: number;
  if (typeof av === "string" && typeof bv === "string") {
    cmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: "base" });
  } else {
    cmp = av < bv ? -1 : av > bv ? 1 : 0;
  }
  return dir === "asc" ? cmp : -cmp;
}

/** Returns a new sorted array; the input is never mutated. */
export function sortOrders(
  orders: readonly WaitlistOrder[],
  key: string,
  type: ColumnType,
  dir: SortDir,
): WaitlistOrder[] {
  return [...orders].sort((a, b) => compareOrders(a, b, key, type, dir));
}

export function columnType(key: string): ColumnType {
  return ORDER_COLUMNS.find((c) => c.key === key)?.type ?? "text";
}
